<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import { ExperimentCookieName, ExperimentName, ExperimentValue } from '@core/types'
import type { components as StrapiComponents } from '@core/types/clients/strapi'

definePageMeta({
    layout: false,
    middleware: [
        'landing-page',
        'home-landing-page',
    ],
    layoutBackgroundColor: '#1181c9',
})

const experiment = useExperiment({ cookieName: ExperimentCookieName.LANDING_PAGE, experimentName: ExperimentName.LANDING_PAGE })
const experimentType = computed(() => (experiment.value || ExperimentValue.A) as ExperimentValue)

const { data: pageData } = await useStrapiPage('frontpage')

const { getHead, getSeoMeta } = useStrapiSeo(
    pageData.value?.seo || {
        metaTitle: 'Play Fun Slots & Bingo at Happy Tiger Online Casino',
        metaDescription: 'Join Happy Tiger™ Today. Play Exclusive Slots Online & New Bingo Games. BeGambleAware.org. T&Cs apply. 18+. Play Safe. Sign Up Now:',
    }
)
const { signupBundles } = await useSignupBundles({ server: true, lazy: false })

useHead(getHead())

useSeoMeta(getSeoMeta())

const strapiHeaderContent = pageData.value?.dynamicContent?.find(
    (content): content is StrapiComponents['schemas']['SharedHeaderComponent'] => content.__component === 'shared.header'
)

// Fallback content for video and images until we decide how we wanna store and manage these assets
const fallbackContent = {
    backgroundColor: strapiHeaderContent?.backgroundColor || '#1181c9',
    videoId: '22b2d6aa3f8cf5456f047d27eb568034',
    trustpilotEnabled: strapiHeaderContent?.trustpilotEnabled,
    usps: strapiHeaderContent?.usp
}
</script>

<template>
    <div :class="`landing-page landing-page--${experiment}`" :data-landing-test="experiment || 'a'">
        <UIApp>
            <LandingPageNavBar />

            <main class="main">
                <LandingPageHeaderContainer
                    :type="experimentType"
                    :offers="signupBundles || []"
                    fallback-path="/lobby"
                    :content="fallbackContent"
                    background-color="#1181c9"
                />

                <StickyTerms
                    title="Significant Conditions"
                    is-landing-page
                    :sticky-config="{ container: '.landing-page', minHeight: 48, offset: 50 }"
                />

                <LazyVisibleNimbusStrapiDynamicRenderer
                    v-if="!isEmpty(pageData?.dynamicContent)"
                    :content="pageData?.dynamicContent"
                    :max-width="1024"
                    color="#fff"
                    hydrate-on-visible
                />

                <section class="section section--dark join__terms">
                    <div class="section__content">
                        <StickyTerms
                            title="Bonus terms"
                            is-landing-page
                            force-full-terms
                        />
                    </div>
                </section>
            </main>

            <div class="landing-page__footer">
                <LazyVisibleFooterContainer :enable-terms="false" is-landing-page hydrate-on-visible />
            </div>
        </UIApp>
    </div>
</template>

<style scoped lang="scss">
$body-bg-light: #1181c9;
$body-bg-dark: #0a5f8a;
$yellow: #f6d701;
$content-width: 1024px;

.landing-page {
    --landing-page-bg: #{$body-bg-light};
    --sticky-terms-background: #1181c9;
    --sticky-terms-color: white;
    --login-button-color: #fff;
    background-color: var(--landing-page-bg);

    .theme-provider {
        --background: var(--landing-page-bg);
    }

    &__footer {
        padding-top: 50px;
        background-color: $body-bg;
    }
}

.dynamic-content {
    > :deep(div:has(.usp-bar, .winners-sidebar)) {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    > :deep(div:not(.centered-text)) {
        padding-left: 15px;
        padding-right: 15px;
    }

    > :deep(div:not(.panel__body)) {
        padding-top: 40px;
        padding-bottom: 40px;

        @media (min-width: $screen-lg) {
            padding-top: 50px;
            padding-bottom: 50px;
        }
    }

    :deep(.media-text__content) {
        text-align: center;

        @include tablet(md) {
            text-align: left;
        }
    }

    :deep(.text) {
        max-width: 800px;
    }

    :deep(.blog-posts) {
        max-width: 800px;
    }

    :deep(.media-text__text) {
        a {
            color: inherit;
        }
    }
    :deep(.media-text__title) {
        a {
            color: inherit;
        }
    }
}

.main {
    .section {
        &__content {
            width: 100%;
            max-width: $content-width;
            margin: auto;
        }

        &--dark {
            background-color: var(--body-bg-dark);
        }
    }

    a:not(.btn) {
        color: inherit;
        text-decoration: underline;
    }

    .join {
        padding-top: 2rem;
        padding-bottom: 3rem;
        color: $white;
        text-align: center;

        @media (min-width: $screen-md) {
            padding-bottom: 4rem;
        }

        .section__content {
            display: grid;
            grid-template-columns: 1fr;
            justify-items: center;
        }

        &__terms {
            --sticky-terms-color: white;

            padding-bottom: 3rem;

            @media (min-width: $screen-md) {
                padding-bottom: 4rem;
            }

            :deep(.offer-terms__title) {
                margin-bottom: 3px;
                font-size: 12px;
            }
        }
    }
}

.footer {
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 2rem 2rem 2rem;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
    }
}
</style>
